import { observer } from 'mobx-react-lite';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FaMinusCircle, FaPlusCircle } from 'react-icons/fa';
import { Spinner } from 'reactstrap';
import CountrySelectService from '../../components/CSC/CountrySelect/CountrySelectService';
import ButtonGroupNew from '../../pages/Form/forms/BusinessLiteForms/components/ButtonGroupNew';
import { DialogContext } from '../../store/context/DialogContext';
import { useTranslation } from '../../store/context/TranslationContext';
import {
  PayNowFooter,
  STATUS,
  formatCurrency,
  formatCurrencyAsZero,
  getCart,
  getCartWithIndividualData,
  paymentCopy,
  updateIndividualCardData
} from './CartService';
import DepositAmountField from './DepositAmountField';
import AddDebitCardModal from './AddDebitCardModal';
import { doGET } from '../../util/HttpUtil';

const IndividualCartItem = ({ individualCartItem, isFree, onRemoveCard }) => {
  const { t } = useTranslation();
  const name = `${individualCartItem?.individualData?.firstName ?? ""} ${individualCartItem?.individualData?.middleName ?? ""} ${individualCartItem?.individualData?.lastName ?? ""}`.trim();

  return (
    <div className='fv_amount_due_row'>
      <span className='d-flex gap-2 align-items-center'>
        {!isFree && (
          <FaMinusCircle
            size={20}
            color='#939393'
            onClick={() => onRemoveCard(individualCartItem)}
            style={{ cursor: 'pointer' }}
          />
        )}
        <span>{name} (Business User)</span>
      </span>
      <span>
        {isFree ? (
          <span style={{ fontWeight: "700" }}>{t('Included')}</span>
        ) : (
          formatCurrency((individualCartItem?.price / 100).toFixed(2))
        )}
      </span>
    </div>
  );
};

const BPlusCart = ({
  data,
  applicationId,
  cartId,
  onPayNow,
  paymentStatus,
  onBackClick,
  totalPaidAmount,
  handleUpdateFormData,
  applicationFormData,
}) => {
  const { t } = useTranslation();
  const { showError } = useContext(DialogContext);
  const [cart, setCart] = useState({});
  const [loading, setLoading] = useState(false);
  const [isAddCardModalOpen, setIsAddCardModalOpen] = useState({ modal: false, isFree: false });
  const { COUNTRY_MASTER_KEYS, cyclosCountryMasterMap } = CountrySelectService;
  const [errors, setErrors] = useState({})
  const [sysConfigForCardMandatoryForBPlus, setSysConfigForCardMandatoryForBPlus] = useState(false);

  const country = data?.Business_Registration_Country;

  const {
    B_PLUS_APPLICATION_FEE: appFeeKey,
    B_PRIME_APPLICATION_FEE: primeAppFeeKey,
    B_PLUS_CARD_FEE: cardFeeKey,
    B_PRIME_CARD_FEE: primeCardFeeKey,
    B_PLUS_ACCOUNT_OPENING_FEE: accountOpeningFeeKey,
    B_PRIME_ACCOUNT_OPENING_FEE: primeAccountOpeningFeeKey,
    VISA_CARD: visaKey,
    COUNTRY: countryKey,
    OPTIONAL_MAXIMUM_DEPOSIT_VIA_STRIPE: maxKey,
    OPTIONAL_MINIMUM_INITIAL_DEPOSIT_VIA_STRIPE: minKey
  } = COUNTRY_MASTER_KEYS;


  const fees = useMemo(() => ({
    visaAvailable: applicationFormData?.extension?.countryMasterMap?.[country]?.[visaKey] === 'Yes',
    minInitialDepositAmount: applicationFormData?.extension?.countryMasterMap?.[country]?.[minKey],
    maxInitialDepositAmount: applicationFormData?.extension?.countryMasterMap?.[country]?.[maxKey],
  }), [cart, country, visaKey, JSON.stringify(applicationFormData?.extension?.countryMasterMap), minKey, maxKey]);

  const cartItems = useMemo(() => ({
    freeCard: cart?.cartItems?.find(item => item?.individualData?.cardFree && [cardFeeKey, primeCardFeeKey]?.includes(item.name)),
    paidCard: cart?.cartItems?.filter(item => !item?.individualData?.cardFree && [cardFeeKey, primeCardFeeKey]?.includes(item.name)),
    accountOpeningFee: cart?.cartItems?.find(item => [accountOpeningFeeKey, primeAccountOpeningFeeKey]?.includes(item.name)),
    applicationFee: cart?.cartItems?.find(item => [appFeeKey, primeAppFeeKey].includes(item?.name)),
    depositAmount: cart?.cartItems?.find(item => item?.name == "Deposit Amount"),
  }), [cart, appFeeKey, primeAppFeeKey, accountOpeningFeeKey, primeAccountOpeningFeeKey, cardFeeKey, primeCardFeeKey]);

  const canAddMoreCards = useMemo(() => {
    const totalIndividuals = data?.individuals?.filter(v => !v?.cardFree && v?.isAuthorizedUser)?.length || 0;
    const totalCards = cart?.cartItems?.filter(item => !item?.individualData?.cardFree && [cardFeeKey, primeCardFeeKey]?.includes(item.name))?.length || 0;
    return totalCards < totalIndividuals;
  }, [data, cart, cardFeeKey, primeCardFeeKey]);


  const getSysConfigForCardMandatoryForBPlus = async () => {
    try {
      const response = await doGET("/api/sys-config/cardMandatoryForBPlus/detail");
      if (response?.status === 200) {
        setSysConfigForCardMandatoryForBPlus(response.data?.cardMandatoryForBPlus ?? false);
      }
    } catch (error) {

    }
  }



  const fetchData = async () => {
    setLoading(true);
    try {
      const freshCart = await getCart(applicationId, cartId);
      setCart(getCartWithIndividualData({
        cart: freshCart,
        businessPlus: data
      }));
      getSysConfigForCardMandatoryForBPlus();
    } catch (err) {
      showError(err);
    } finally {
      setLoading(false);
    }
  };



  useEffect(() => {
    if (cartId) fetchData();
  }, [applicationId, cartId, JSON?.stringify(data)]);


  const handleAddCard = async ({ individual_id, address, isFree = false }) => {
    setLoading(true);
    try {
      const updatedIndividuals = data.individuals.map(ind =>
        ind._id === individual_id ? {
          ...ind,
          card: 1,
          mailingAddress: address,
          cardFree: isFree
        } : ind
      );

      await handleUpdateFormData({
        ...data,
        individuals: updatedIndividuals
      });

    } catch (err) {
      showError(err.message);
    } finally {
      setLoading(false);
    }
  }

  const handleRemoveCard = async (cartItem) => {
    setLoading(true);
    try {
      const updatedIndividuals = data.individuals.map(ind =>
        ind._id === cartItem.individualData._id ? {
          ...ind,
          card: 0,
          mailingAddress: {},
          cardFree: false
        } : ind
      );

      await handleUpdateFormData({
        ...data,
        individuals: updatedIndividuals
      });

    } catch (err) {
      showError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDepositAmountChange = async (initialDepositAmount) => {
    setLoading(true);
    try {
      await handleUpdateFormData({
        ...data,
        initialDepositAmount
      });
    } catch (err) {
      showError(err);
    } finally {
      setLoading(false);
    }
  }

  const showAdditionalCardSection = useMemo(() => {
    return (data?.individuals?.length || 0) > 1;
  }, [data]);

  const handleNextClick = () => {
    try {
      if (sysConfigForCardMandatoryForBPlus && !cartItems?.freeCard && fees?.visaAvailable) {
        showError(t('Please add a Visa Business Card (Included)'));
        return;
      }
      if (errors?.initialDepositAmount) {
        showError(errors?.initialDepositAmount);
        return;
      }
      onPayNow(cart?.payable == 0)
    } catch (error) {

    }
  }

  return (
    <div className={`d-flex flex-column justify-content-center align-items-center`}>
      {loading && (
        <div className="fv_amount_due_full_screen_loader">
          <Spinner color="primary" />
        </div>
      )}

      <div className='fv_amount_due'>
        <div className='fv_amount_due_header'>
          {t('Application Fees')} ({cyclosCountryMasterMap?.[country]?.[countryKey]})
        </div>

        {cartItems.accountOpeningFee && <div className="fv_amount_due_row">
          <span>{t('Account Opening Fee')}</span>
          <span>{formatCurrency((cartItems.accountOpeningFee?.price / 100).toFixed(2))}</span>
        </div>}

        {cartItems.applicationFee && <div className="fv_amount_due_row">
          <span>{t('Application Fee')}</span>
          <span>{formatCurrencyAsZero((cartItems.applicationFee?.price / 100).toFixed(2))}</span>
        </div>}


        {fees?.visaAvailable && <div className="fv_amount_due_row">
          <span className='fw-600'>{t('Visa Business Card (Included) ')}</span>
          {cartItems.freeCard ? (
            <FaMinusCircle
              size={20}
              color="#939393"
              onClick={() => handleRemoveCard(cartItems.freeCard)}
              style={{ cursor: 'pointer' }}
            />
          ) : (
            <FaPlusCircle
              size={20}
              color="#000"
              onClick={() => setIsAddCardModalOpen({
                modal: true,
                isFree: true
              })}
              style={{ cursor: 'pointer' }}
            />
          )}
        </div>}

        {cartItems.freeCard && fees?.visaAvailable && (
          <IndividualCartItem
            individualCartItem={cartItems.freeCard}
            isFree
          />
        )}


        {showAdditionalCardSection && fees?.visaAvailable && (
          <div className="fv_amount_due_row top-border-dashed">
            <span className='fw-600' style={{ color: cartItems.freeCard ? 'inherit' : '#939393' }}>
              {t('Additional Visa Business Card')}
            </span>
            {canAddMoreCards && cartItems.freeCard ? (
              <FaPlusCircle
                size={20}
                color="#000"
                onClick={() => setIsAddCardModalOpen({
                  modal: true,
                  isFree: false
                })}
                style={{ cursor: 'pointer' }}
              />
            ) : (
              <FaPlusCircle
                size={20}
                color="#939393"
                style={{ cursor: 'not-allowed' }}
              />
            )}
          </div>
        )}

        {fees?.visaAvailable && cartItems.paidCard?.map((item, index) => (
          <IndividualCartItem
            key={index}
            individualCartItem={item}
            onRemoveCard={handleRemoveCard}
          />
        ))}

        <div className="fv_amount_due_total ">
          <span>{t('Initial Deposit Amount')}</span>
          <DepositAmountField
            minInitialDepositAmount={fees?.minInitialDepositAmount}
            maxInitialDepositAmount={fees?.maxInitialDepositAmount}
            value={cartItems?.depositAmount?.price}
            handleDepositAmountChange={handleDepositAmountChange}
            loading={loading || paymentStatus === STATUS?.PROCESSING}
            onError={(errorMsg) => setErrors((prevErrors) => ({ ...prevErrors, initialDepositAmount: errorMsg }))}
          />
        </div>
        {/* <div className='fv_amount_init_deposit_offer'>
          For faster operational readiness, please select a deposit amount
        </div> */}

        {cart?.payable != cart?.total && <div>
          <div className='fv_amount_due_total'>
            <span>{t("Total Due")}</span>
            <span>{formatCurrencyAsZero((cart?.total / 100).toFixed(2))}</span>
          </div>

          {totalPaidAmount && <div className=' fv_amount_due_row mt-2 success '>
            <span>{"Amount Paid"}</span>
            <span className='d-flex align-items-center gap-2 '> {formatCurrencyAsZero(((totalPaidAmount) / 100).toFixed(2))}</span>
          </div>}
        </div>}

        <div className='fv_amount_due_total due '>
          <span>{cart?.payable != cart?.total ? t("Balance Due") : t("Total Due")}</span>
          <span>{formatCurrencyAsZero((cart?.payable / 100).toFixed(2))}</span>
        </div>

        <PayNowFooter />
      </div>

      <div className='w-100'>
        <ButtonGroupNew
          onNextClick={handleNextClick}
          onBackClick={() => { onBackClick() }}
          step={4}
          isDisabled={paymentStatus === STATUS?.PROCESSING || loading}
          nextText={cart?.payable == 0 ? t(paymentCopy?.submit_application) : t(paymentCopy?.pay_now)}
          asLabel={false}
          loading={paymentStatus === STATUS?.PROCESSING}
        />
      </div>

      {isAddCardModalOpen?.modal && (
        <AddDebitCardModal
          businessPlusData={data}
          existingCartItems={cart?.cartItems || []}
          onClose={() => setIsAddCardModalOpen({ modal: false, isFree: false })}
          handleOrderCard={handleAddCard}
          isFree={isAddCardModalOpen?.isFree}
        />
      )}


    </div>
  );
};

export default observer(BPlusCart);
